<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <main>
        <div v-if="Object.keys(order).length" class="ion-padding">
          <div class="ion-text-center">
            <img src="../assets/images/KREWE.svg"/>
          </div>
          <div class="ion-text-center ion-padding-top">
            <template v-if="isOrderUpdated">
              <h1 class="ion-text-uppercase">{{ $t("Order Confirmed") }}</h1>
              <ion-icon size="large" color="success" :icon="checkmarkCircle"/>
            </template>
            <template v-if="isOrderCancelled">
              <h1 class="ion-text-uppercase">{{ $t("Order Cancelled") }}</h1>
            </template>
          </div>
          <ion-item lines="none" class="ion-text-center ion-margin-bottom">
            <ion-label>
              <h1>{{ order.orderName }}</h1>
            </ion-label>
          </ion-item>
          <ion-item lines="none" v-if="getMessage()" class="ion-margin-bottom">
            <ion-label class="ion-text-center" v-html="getMessage()"></ion-label>
          </ion-item>
          <ion-card>
            <ion-item lines="none" class="border">
              <ion-label>
                {{ order.customerName }}
                <p>{{ order.id }}</p>
              </ion-label>
              <ion-note slot="end">{{ $filters.formatDate(order.orderDate) }}</ion-note>
            </ion-item>
          </ion-card>
          <div v-if="order.statusId !== 'ORDER_CANCELLED' && order.shipGroup.length > 0" >
            <div v-for="(shipGroup, index) of order.shipGroup" :key="index">
              <ion-card>
                <ion-item v-show="item.status !== 'ITEM_CANCELLED'" v-for="item of shipGroup.items" :key="item.id" lines="full">
                  <ion-thumbnail slot="start">
                    <Image :src='getProduct(item.productId).mainImageUrl' />
                  </ion-thumbnail>
                  <ion-label slot="start">
                    <p>{{ item.brandName }}</p>
                    <h2>{{ item.name }}</h2>
                    <p v-for="(attribute, feature) in ($filters.groupFeatures(getProduct(item.productId).featureHierarchy))" :key="attribute" >
                      <span class="sentence-case">{{ feature }}</span>: {{ attribute }}
                    </p>
                  </ion-label>
                </ion-item>
                <ion-item lines="full" v-if="!isOrderCancelled">
                  <ion-label class="ion-text-uppercase">
                    {{ $t("Estimated Delivery") }}
                  </ion-label>
                  <ion-label slot="end">
                    {{ getTwoDeliveryDate() }}
                  </ion-label>
                </ion-item>
                <ion-item v-if="shipGroup.trackingNumber">
                  <ion-label>{{ $t('Tracking code') }}</ion-label>
                  <ion-note slot="end">{{ shipGroup.trackingNumber }}</ion-note>
                </ion-item>
              </ion-card>
              <ion-button v-if="!(isOrderUpdated || isOrderCancelled)" class="primary-btn ion-margin-vertical" @click="updateShippingAddress(shipGroup)" expand="block">{{ $t("Ship it with two day delivery")}}</ion-button>
              <ion-button v-if="hasPermission(Actions.APP_SHPGRP_CNCL)" expand="block" @click="cancel(shipGroup)" fill="clear" color="danger">{{ $t("Cancel") }}</ion-button>
            </div>
          </div>
          <div v-else-if="!(isOrderCancelled || isOrderUpdated)" class="ion-text-center ion-padding-top">
            <ion-label>{{ $t("Order item not eligible for reroute fulfilment") }}</ion-label>
          </div>
          <ion-button expand="block" v-if="isOrderCancelled || isOrderUpdated || (order.statusId === 'ORDER_CANCELLED' && order.shipGroup.length <= 0)" class="primary-btn ion-margin-vertical" @click="redirectToStore">{{ $t("Continue shopping") }}</ion-button>
        </div>
        <div v-else-if="loader == null" class="ion-text-center ion-padding-top">
          <ion-label>{{ $t("Order not found") }}</ion-label>
        </div>
      </main>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  alertController,
  IonButton,
  IonCard,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonNote,
  IonPage,
  IonThumbnail,
  loadingController,
} from "@ionic/vue";
import { defineComponent } from "vue";
import { useRouter } from "vue-router";
import { useStore } from 'vuex'
import { OrderService } from "@/services/OrderService";
import i18n, { translate } from "@/i18n";
import { hasError, showToast, getTwoDeliveryDate } from "@/utils";
import Image from "@/components/Image.vue";
import { ProductService } from "@/services/ProductService";
import { Actions, hasPermission } from '@/authorization'
import { initialise } from '@/adapter'
import { checkmarkCircle } from "ionicons/icons"

export default defineComponent({
  name: "Order",
  components: {
    Image,
    IonButton,
    IonCard,
    IonContent,
    IonIcon,
    IonItem,
    IonLabel,
    IonNote,
    IonThumbnail,
    IonPage,
  },
  data() {
    return {
      loader: null as any,
      maxAge: process.env.VUE_APP_CACHE_MAX_AGE ? parseInt(process.env.VUE_APP_CACHE_MAX_AGE) : 0,
      order: {} as any,
      products: {} as any,
      isOrderUpdated: false,
      omsUrl: this.$route.query.oms,
      isOrderCancelled: false
    }
  },
  props: ["token"],
  async mounted() {
    if (Object.keys(this.$route.query).length > 0) {
      if(!this.$route.query.oms || !this.token) {
        // invalid request
        return;
      }
      initialise({
        token: "",  // Not passing token, as in this app we don't want to send the token in the Authorizartion header and instead the token will be passed in the params/body
        instanceUrl: `${this.$route.query.oms}/api/`,
        cacheMaxAge: this.maxAge,
        events: {
          responseError: () => {
            setTimeout(() => this.dismissLoader(), 100);
          }
        }
      })
      this.store.dispatch("user/setUserInstanceUrl", `${this.$route.query.oms}/api/`)
      await this.getOrder();
    }
  },
  methods: {
    async presentLoader() {
      this.loader = await loadingController
        .create({
          message: this.$t("Fetching order details."),
          translucent: true,
        });
      await this.loader.present();
    },
    dismissLoader() {
      if (this.loader) {
        this.loader.dismiss();
        this.loader = null;
      }
    },
    async getOrder() {
      await this.presentLoader()
      let resp;
      let order
      if(!this.token) {
        return;
      }

      try {
        resp = await OrderService.getOrder({
          token: this.token
        });
        if (!hasError(resp) && resp.data.id) {
          order = resp.data;
          i18n.global.locale = order.localeString || "en";
          const productIds: any = new Set();
          order.shipGroup = order.shipGroup.filter((group: any) => {
            if(group.facilityId === "REJECTED_ITM_PARKING" && group.shipmentMethodTypeId === "SHIPSI_SM_DAY_DLV") {
              group.items = group.items.filter((item: any) => {
                if (item.status == "ITEM_CANCELLED") return false;
                productIds.add(item.productId);
                return true;
              })
              return group.items.length > 0;
            }
          })
          if (productIds.length) await this.fetchProducts([...productIds])
          await this.store.dispatch("user/getConfiguration", { productStoreId: order.productStoreId, token: this.token});
          this.order = order;
          if (productIds.size) await this.fetchProducts([...productIds])
        }
      } catch (error) {
        console.error(error)
      }
      this.dismissLoader()
    },
    async fetchProducts(productIds: any) {
      const productIdFilter = productIds.reduce((filter: string, productId: any) => {
        if (filter !== '') filter += ' OR '
        return filter += productId;
      }, '');

      try {
        const resp = await ProductService.fetchProducts({
          "filters": ['productId: (' + productIdFilter + ')'],
          "viewSize": productIds.length
        })

        if (resp.status === 200 && !hasError(resp) && resp.data) {
          resp.data.response.docs.forEach((product: any) => {
            this.products[product.productId] = product
          });
        }
      } catch (error) {
        console.error(error)
      }
    },

    getProduct(productId: string) {
      return this.products[productId] ? this.products[productId] : {}
    },

    async updateShippingAddress(shipGroup: any) {
      let resp
      const payload = {
        "orderId": this.order.id,
        "shipGroupSeqId": shipGroup.shipGroupSeqId,
        "contactMechId": shipGroup.shipTo.postalAddress.id,
        "shipmentMethod": "SECOND_DAY@_NA_",
        "contactMechPurposeTypeId": "SHIPPING_LOCATION",
        "toName": `${shipGroup.shipTo.postalAddress.toName}`,
        "address1": shipGroup.shipTo.postalAddress.address1,
        "city": shipGroup.shipTo.postalAddress.city,
        "stateProvinceGeoId": shipGroup.shipTo.postalAddress.stateProvinceGeoId,
        "postalCode": shipGroup.shipTo.postalAddress.postalCode,
        "countryGeoId": shipGroup.shipTo.postalAddress.countryGeoId,
        "token": this.token
      } as any

      payload.isEdited = false

      try {
        resp = await OrderService.updateShippingAddress(payload);
        if (resp.status === 200 && !hasError(resp) && resp.data) {
          showToast(translate("Changes saved"))
          this.isOrderUpdated = true
        } else {
          showToast(translate("Failed to update the shipping address"))
        }
      } catch (error) {
        console.error(error)
        showToast(translate("Failed to update the shipping address"))
      }
      this.getOrder();
    },

    async cancelShipGroup(shipGroup: any) {
      let resp
      const itemReasonMap = {} as any
      shipGroup.items.map((item: any) => itemReasonMap[item.itemSeqId] = 'OICR_CHANGE_MIND')
      const payload = {
        "orderId": this.order.id,
        "shipGroupSeqId": shipGroup.shipGroupSeqId,
        "itemReasonMap": itemReasonMap,
        "token": this.token
      } as any

      try {
        resp = await OrderService.cancelOrderItem(payload);
        if (resp.status === 200 && !hasError(resp) && resp.data.orderId == this.order.id) {
          shipGroup.isCancelled = true;
          this.isOrderCancelled = true
          showToast(translate("Order cancelled successfully"))
        } else {
          showToast(translate("Failed to cancel the order"))
        }
      } catch (error) {
        console.error(error)
        showToast(translate("Failed~ to cancel the order"))
      }
      this.getOrder();
    },

    async cancel(shipGroup: any) {
      const message = this.$t("Are you sure you want to cancel the order items?");
      const alert = await alertController.create({
        header: this.$t("Cancel items"),
        message,
        buttons: [
          {
            text: this.$t("Don't Cancel"),
          },
          {
            text: this.$t("Cancel"),
            handler: () => {
              this.cancelShipGroup(shipGroup);
            }
          }
        ],
      });
      return alert.present();
    },
    redirectToStore() {
      window.open("https://www.krewe.com/", "_blank", "noopener, noreferrer")
    },
    getMessage() {
      return this.isOrderUpdated ?
      this.$t("You have chosen to ship your order with 2-day delivery. Your new estimated delivery date will be.", { space: "<br/>", date: getTwoDeliveryDate() }) :
      this.isOrderCancelled ?
      this.$t("You have chosen to cancel your order. A confirmation email will be sent shortly.", { space: "<br/>" }) :
      ""
    }
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    return {
      Actions,
      checkmarkCircle,
      hasPermission,
      router,
      store,
      getTwoDeliveryDate
    };
  }
});
</script>

<style scoped>

.primary-btn {
  --background: #677967;
  --border-radius: 0;
}

@media (min-width: 700px) {
  main {
    max-width: 400px;
    margin: auto;
  }
}
</style>